import React, { useCallback, useState, useRef } from 'react';
import classNames from 'classnames';
import {
  ElementPosition,
  HORIZONTAL_POSITION,
  HorizontalPosition,
} from 'models/Position';
import Portal from 'components/Portal';
import { AUTH_ROUTE } from 'data/global';
import { useLocation } from 'react-router-dom';

import './DropdownOnHover.styles.scss';

const PADDING = 16;

type DropdownOnHoverProps = {
  className?: string;
  triggerContent: React.ReactNode;
  triggerClasses?: string;
  position?: HorizontalPosition;
  onTriggerAction?: () => void;
};

const DropdownOnHover: React.FC<DropdownOnHoverProps> = (props) => {
  const {
    children,
    className,
    triggerContent,
    position = HORIZONTAL_POSITION.LEFT,
    onTriggerAction,
  } = props;

  const [isOpen, setIsOpen] = useState(true);
  const [menuPosition, setMenuPosition] = useState<ElementPosition | null>(
    null,
  );

  const triggerRef = useRef<HTMLDivElement>(null);

  const pathname = useLocation().pathname;

  const classes = classNames('dropdown-on-hover', { open: isOpen }, className);

  const onHoverEnter = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setMenuPosition({
      top: rect.bottom + PADDING,
      left: position === HORIZONTAL_POSITION.LEFT ? rect.left : rect.right,
    });

    setIsOpen(true);
  }, [position]);

  const onHoverLeave = useCallback(() => {
    setMenuPosition(null);

    setIsOpen(false);
  }, []);

  return (
    <div
      className={classes}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={onHoverLeave}
    >
      <div
        ref={triggerRef}
        className="dropdown-on-hover__trigger"
        {...(onTriggerAction && { onClick: onTriggerAction })}
      >
        {triggerContent}
      </div>

      <Portal isOpen={isOpen}>
        <div
          className={classNames(
            'dropdown-on-hover__children',
            { auth: pathname.includes(AUTH_ROUTE) },
            position,
          )}
          style={{
            top: menuPosition?.top,
            left: menuPosition?.left,
          }}
        >
          <div>{children}</div>
        </div>
      </Portal>
    </div>
  );
};

export default DropdownOnHover;
